import { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';
import { Save, RotateCcw, ExternalLink, Sparkles, ChevronDown, ChevronRight } from 'lucide-react';
import Header from '../components/Header';

// Everything editable. Grouped by page. Add new fields here in future to expand
// the editor to more pages.
const CONTENT_SCHEMA = [
  {
    id: 'home',
    label: 'Home page',
    preview: '/',
    sections: [
      {
        heading: 'Hero',
        fields: [
          { key: 'home.hero.wheretonext', label: 'Small label above the hero ("Where to next? Try —")', type: 'short', default: 'Where to next? Try —' },
          { key: 'home.hero.title', label: 'Main headline', type: 'long', default: 'Restore Your Relationships Through Faith' },
          { key: 'home.hero.subtitle', label: 'Subtitle under the headline', type: 'long', default: 'Find guidance, wisdom, and biblical insight for every relationship in your life. From dating to marriage, family to friendships, discover how to build stronger, more meaningful connections.' },
          { key: 'home.hero.cta', label: 'Button text', type: 'short', default: 'Start Your Journey' },
        ],
      },
      {
        heading: 'Sanctuary teaser card (gold banner)',
        fields: [
          { key: 'home.sanctuary_teaser.pill', label: 'Small pill above title', type: 'short', default: 'New · Members Only' },
          { key: 'home.sanctuary_teaser.title', label: 'Title', type: 'short', default: 'Step into The Sanctuary' },
          { key: 'home.sanctuary_teaser.body', label: 'Description', type: 'long', default: 'A private prayer wall, articles inspired by your requests, and a soft place to land — for $6.99/month.' },
          { key: 'home.sanctuary_teaser.cta', label: 'Button', type: 'short', default: 'Take a Look' },
        ],
      },
      {
        heading: 'Verse of the Week',
        fields: [
          { key: 'home.verse.label', label: 'Small label above the verse', type: 'short', default: 'Verse of the Week' },
        ],
      },
      {
        heading: 'Intro video section',
        fields: [
          { key: 'home.intro_video.title', label: 'Section title', type: 'short', default: 'A Warm Welcome' },
          { key: 'home.intro_video.subtitle', label: 'Subtitle', type: 'short', default: 'A personal message from Genesis' },
        ],
      },
      {
        heading: 'Explore by Category',
        fields: [
          { key: 'home.categories.title', label: 'Section title', type: 'short', default: 'Explore by Category' },
          { key: 'home.categories.subtitle', label: 'Subtitle', type: 'short', default: 'Choose a path that resonates with your journey' },
        ],
      },
      {
        heading: 'Restoration Stories',
        fields: [
          { key: 'home.testimonials.title', label: 'Section title', type: 'short', default: 'Restoration Stories' },
          { key: 'home.testimonials.subtitle', label: 'Subtitle', type: 'short', default: 'Real hearts, real healing' },
          { key: 'home.testimonials.cta', label: 'Button text', type: 'short', default: 'Read More Stories & Share Yours' },
        ],
      },
    ],
  },
  {
    id: 'sanctuary',
    label: 'The Sanctuary page',
    preview: '/sanctuary',
    sections: [
      {
        heading: 'Hero',
        fields: [
          { key: 'sanctuary.hero.pill', label: 'Small badge above the title', type: 'short', default: 'Introducing a quieter space' },
          { key: 'sanctuary.hero.title', label: 'Main title', type: 'short', default: 'The Sanctuary' },
          { key: 'sanctuary.hero.tagline', label: 'Tagline (below title)', type: 'long', default: 'A quieter, more intimate space for believers walking the same road.' },
          { key: 'sanctuary.hero.assurance', label: 'Small line under the CTA buttons', type: 'long', default: 'All existing articles, the feelings guide, and the public prayer wall remain 100% free. The Sanctuary is a deeper, optional layer.' },
        ],
      },
      {
        heading: '"What\'s inside" section',
        fields: [
          { key: 'sanctuary.benefits.title', label: 'Section title', type: 'short', default: "What's inside the Sanctuary" },
          { key: 'sanctuary.benefits.subtitle', label: 'Section subtitle', type: 'short', default: 'A small, intentional space — not more content, but more connection.' },
          { key: 'sanctuary.benefits.card1.title', label: 'Card 1 · Title', type: 'short', default: 'Private prayer wall' },
          { key: 'sanctuary.benefits.card1.body', label: 'Card 1 · Description', type: 'long', default: 'Post intentions only members can see. Others pray over you — you pray over them. Held quietly, prayerfully.' },
          { key: 'sanctuary.benefits.card2.title', label: 'Card 2 · Title', type: 'short', default: 'Shape what gets written' },
          { key: 'sanctuary.benefits.card2.body', label: 'Card 2 · Description', type: 'long', default: "Share the topics closest to your heart. Your voice guides what I write next — and you may find you're not alone in what you're carrying." },
          { key: 'sanctuary.benefits.card3.title', label: 'Card 3 · Title', type: 'short', default: 'A closer circle' },
          { key: 'sanctuary.benefits.card3.body', label: 'Card 3 · Description', type: 'long', default: 'Fewer voices, more depth. A small community of believers walking the same road, encouraging each other with scripture.' },
        ],
      },
      {
        heading: 'Pricing card',
        fields: [
          { key: 'sanctuary.pricing.pill', label: 'Pill label at the top', type: 'short', default: 'Members Only' },
          { key: 'sanctuary.pricing.title', label: 'Product name', type: 'short', default: 'The Sanctuary' },
          { key: 'sanctuary.pricing.assurance', label: 'Reassurance line under the button', type: 'short', default: 'Cancel anytime · Secure Stripe checkout · No commitments' },
        ],
      },
      {
        heading: 'Bottom scripture',
        fields: [
          { key: 'sanctuary.scripture', label: 'Scripture quote', type: 'long', default: '"As iron sharpens iron, so one person sharpens another." — Proverbs 27:17' },
        ],
      },
    ],
  },
  {
    id: 'tools',
    label: 'Sacred Tools page',
    preview: '/tools',
    sections: [
      {
        heading: 'Hero',
        fields: [
          { key: 'tools.hero.pill', label: 'Small pill above title', type: 'short', default: 'Sacred Tools' },
          { key: 'tools.hero.title', label: 'Main title', type: 'short', default: 'Tools for your relationships' },
          { key: 'tools.hero.subtitle', label: 'Subtitle', type: 'long', default: 'Workbooks, scripture cards, and practical guides you can bring straight into your marriage, family, and friendships.' },
          { key: 'tools.hero.member_perk', label: 'Member perk line (only shown to Sanctuary members)', type: 'long', default: 'Sanctuary members save on every tool — your discount is auto-applied at checkout.' },
        ],
      },
      {
        heading: 'Empty state (shown when no tools are published)',
        fields: [
          { key: 'tools.empty.title', label: 'Empty state title', type: 'short', default: 'Tools are on their way' },
          { key: 'tools.empty.body', label: 'Empty state description', type: 'long', default: 'Genesis is preparing the first collection. Check back soon.' },
        ],
      },
      {
        heading: 'Suggest-a-tool card',
        fields: [
          { key: 'tools.suggest.title', label: 'Title', type: 'short', default: 'Suggest a tool' },
          { key: 'tools.suggest.body', label: 'Description', type: 'long', default: "Is there a workbook, printable, or scripture-anchored guide you wish existed? Tell Genesis what you'd want, and what feels fair to pay for it ($2 – $10). She'll create it if it resonates with what others are asking for too." },
        ],
      },
    ],
  },
  {
    id: 'contact',
    label: 'Contact page',
    preview: '/contact',
    sections: [
      {
        heading: 'Hero',
        fields: [
          { key: 'contact.hero.title', label: 'Main title', type: 'short', default: 'Reach Out' },
          { key: 'contact.hero.subtitle', label: 'Subtitle', type: 'long', default: "Quick questions, feedback, or a note of encouragement — send a short message and I'll get back to you when I can." },
        ],
      },
      {
        heading: 'Info notice (the cream banner above the form)',
        fields: [
          { key: 'contact.notice.title', label: 'Notice title', type: 'short', default: 'Have a question or a note of encouragement?' },
          { key: 'contact.notice.body', label: 'Notice body', type: 'long', default: 'This form is best for quick questions, feedback, or general inquiries. For prayer requests, share on the prayer wall or explore the articles and feelings guide.' },
        ],
      },
      {
        heading: 'Thank-you state (after send)',
        fields: [
          { key: 'contact.sent.title', label: 'Confirmation title', type: 'short', default: 'Your message is on its way' },
          { key: 'contact.sent.body', label: 'Confirmation body', type: 'long', default: "I'll get back to you when I can — usually within a few days." },
        ],
      },
    ],
  },
];

export default function AdminSiteContent() {
  const [values, setValues] = useState({});
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(false);
  const [openSections, setOpenSections] = useState({ home: true, sanctuary: true, tools: true, contact: true });

  useEffect(() => { load(); }, []);
  const load = async () => {
    const { data } = await api.get('/site-content');
    setValues(data || {});
    setDirty({});
  };

  const handleChange = (key, val) => {
    setValues((v) => ({ ...v, [key]: val }));
    setDirty((d) => ({ ...d, [key]: true }));
  };

  const saveAll = async () => {
    const dirtyKeys = Object.keys(dirty).filter((k) => dirty[k]);
    if (dirtyKeys.length === 0) return toast.info('Nothing to save');
    setSaving(true);
    try {
      const items = dirtyKeys.map((k) => ({ key: k, value: values[k] || '' }));
      await api.put('/admin/site-content', { items });
      toast.success(`Saved ${items.length} change${items.length > 1 ? 's' : ''}`);
      setDirty({});
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const resetField = async (key) => {
    if (!window.confirm('Reset this field back to the built-in default?')) return;
    try {
      await api.delete(`/admin/site-content/${encodeURIComponent(key)}`);
      toast.success('Reset');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reset failed');
    }
  };

  const inputStyle = {
    fontFamily: 'Manrope, sans-serif', backgroundColor: '#FFFFFF', border: '1px solid #E6E2D8',
    borderRadius: '10px', padding: '10px 12px', color: '#2C2724', width: '100%', fontSize: '14px',
  };
  const dirtyCount = Object.values(dirty).filter(Boolean).length;

  return (
    <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
      <Header />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 rounded-xl" style={{ backgroundColor: '#E57A4415' }}>
            <Sparkles className="w-5 h-5" style={{ color: '#E57A44' }} />
          </div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }}>
            Edit Site Content
          </h1>
        </div>
        <p className="mb-6" style={{ fontFamily: 'Manrope, sans-serif', color: '#5C564D' }}>
          Change any piece of text on any page. Save applies instantly to your live site — no redeploy needed.
        </p>

        {/* Sticky save bar */}
        <div
          className="sticky top-16 z-10 mb-6 p-3 rounded-2xl flex items-center gap-3"
          style={{ backgroundColor: dirtyCount > 0 ? '#FFF7ED' : '#F5F5F0', border: `1px solid ${dirtyCount > 0 ? '#FDBA74' : '#E6E2D8'}` }}
        >
          <div className="text-sm flex-1" style={{ fontFamily: 'Manrope, sans-serif', color: dirtyCount > 0 ? '#7A5C1E' : '#5C564D' }}>
            {dirtyCount === 0 ? 'No unsaved changes' : `${dirtyCount} unsaved change${dirtyCount > 1 ? 's' : ''}`}
          </div>
          <button
            onClick={saveAll}
            disabled={saving || dirtyCount === 0}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full transition"
            style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#5C715E', color: '#FDFBF7', fontWeight: 700, opacity: (saving || dirtyCount === 0) ? 0.5 : 1, fontSize: '14px' }}
            data-testid="site-content-save-all"
          >
            <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>

        {CONTENT_SCHEMA.map((page) => (
          <div key={page.id} className="mb-6 rounded-2xl" style={{ backgroundColor: '#FFFFFF', border: '1px solid #F0EBE0' }} data-testid={`page-block-${page.id}`}>
            <button
              onClick={() => setOpenSections((s) => ({ ...s, [page.id]: !s[page.id] }))}
              className="w-full flex items-center justify-between p-4 text-left"
              style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724', fontWeight: 600 }}
            >
              <div className="flex items-center gap-3">
                {openSections[page.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                <span className="text-lg">{page.label}</span>
                <a
                  href={page.preview}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1 text-xs"
                  style={{ fontFamily: 'Manrope, sans-serif', color: '#5B7C99', fontWeight: 500 }}
                  data-testid={`preview-${page.id}`}
                >
                  <ExternalLink className="w-3 h-3" /> Preview
                </a>
              </div>
            </button>
            {openSections[page.id] && (
              <div className="px-4 pb-4">
                {page.sections.map((sec) => (
                  <div key={sec.heading} className="mb-6 last:mb-0">
                    <h3 className="text-xs font-bold uppercase tracking-widest mb-3" style={{ fontFamily: 'Manrope, sans-serif', color: '#8C867D' }}>
                      {sec.heading}
                    </h3>
                    <div className="space-y-4">
                      {sec.fields.map((field) => {
                        const currentValue = values[field.key] !== undefined ? values[field.key] : field.default;
                        const isCustomized = values[field.key] !== undefined;
                        return (
                          <div key={field.key} className="p-3 rounded-xl" style={{ backgroundColor: dirty[field.key] ? '#FFF7ED' : '#FAF7F1', border: `1px solid ${dirty[field.key] ? '#FDBA74' : '#EFEAE0'}` }}>
                            <div className="flex items-center justify-between mb-1">
                              <label className="text-sm block" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2724', fontWeight: 600 }}>
                                {field.label}
                              </label>
                              {isCustomized && (
                                <button
                                  onClick={() => resetField(field.key)}
                                  className="text-xs inline-flex items-center gap-1"
                                  style={{ fontFamily: 'Manrope, sans-serif', color: '#8C867D' }}
                                  title="Reset to built-in default"
                                  data-testid={`reset-${field.key}`}
                                >
                                  <RotateCcw className="w-3 h-3" /> Reset
                                </button>
                              )}
                            </div>
                            {field.type === 'long' ? (
                              <textarea
                                rows={3}
                                value={currentValue}
                                onChange={(e) => handleChange(field.key, e.target.value)}
                                style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }}
                                data-testid={`field-${field.key}`}
                              />
                            ) : (
                              <input
                                type="text"
                                value={currentValue}
                                onChange={(e) => handleChange(field.key, e.target.value)}
                                style={inputStyle}
                                data-testid={`field-${field.key}`}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        <div className="text-center text-xs mt-6" style={{ fontFamily: 'Manrope, sans-serif', color: '#8C867D' }}>
          Need to edit copy on another page? Just tell me and I'll add its fields here.
        </div>
      </div>
    </div>
  );
}
